import { Box, FlexBox, Tooltip } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { PropsWithChildren } from 'lib-shared-types-utils';
import { ComponentProps, memo, useMemo } from 'react';
import { MarkRequired } from 'ts-essentials';

import {
  CustomField,
  getCustomSelectFieldDisplayValue,
} from 'utils/customFields';
import { convertTreeToFlatItems } from 'utils/tree';

import styles from './customFieldChip.module.scss';

type CustomFieldChipProps = ComponentProps<typeof FlexBox> & {
  /** The custom field. */
  customField: MarkRequired<CustomField, 'value'>;
};

/**
 * Presents a chip containing value and label. It also shows a tooltip if there
 * is description.
 */
export const CustomFieldChip = memo(function CustomFieldChip({
  className,
  customField,
  ...flexProps
}: CustomFieldChipProps): JSX.Element {
  const { attribute, value } = customField;

  const displayedValue = useMemo(() => {
    if (value.data_type === 'string') {
      return <Value>{value.value}</Value>;
    }

    if ('options' in attribute && value.data_type === 'string_set') {
      const flatOptions = convertTreeToFlatItems(attribute.options, {
        childrenKey: 'options',
      });

      const hasHierarchy = flatOptions.some((option) => option.parent_id);

      if (!hasHierarchy) {
        return (
          <Value>
            {value.value
              .map(
                (value) =>
                  flatOptions.find((option) => option.value === value)?.label,
              )
              .filter(Boolean)
              .join(', ')}
          </Value>
        );
      }

      const displayValue = getCustomSelectFieldDisplayValue(
        { attribute, value },
        { nodeClassName: styles.subdued, separatorClassName: styles.subdued },
      );

      if (!displayValue) {
        return '';
      }
      return (
        <Tooltip
          content={displayValue.rawPath}
          dark
          disabled={!displayValue.isLongPath}
        >
          <Value>{displayValue.path}</Value>
        </Tooltip>
      );
    }
  }, [attribute, value]);

  return (
    <FlexBox
      className={classnames(styles.chip, className)}
      alignItems="center"
      {...flexProps}
    >
      <Tooltip
        content={attribute.description}
        disabled={!attribute.description}
        dark
      >
        <Box className={styles.chipLabel}>{attribute.label}</Box>
      </Tooltip>

      {displayedValue}
    </FlexBox>
  );
});

const Value = ({ children }: PropsWithChildren): JSX.Element => {
  return (
    <Box className={styles.chipValueContainer}>
      <Box as="span" className={styles.chipValue}>
        {children}
      </Box>
    </Box>
  );
};
